import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import UserMenu from "../components/UserMenu";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ClipboardCheck, Eye } from "lucide-react";
import SafetyShell from "../safety/SafetyShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export default function ProductionApprovals({ user, onLogout }) {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/production/reports/pending-approvals`, auth());
      setReports(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
    </div>;
  }

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[
        { label: "Safety", to: "/safety" },
        { label: "Production Reports", to: "/safety/production-reports" },
        { label: "Pending Approvals" },
      ]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Production Reports", to: "/safety/production-reports", depth: 1 },
        { label: "Pending Approvals", active: true, depth: 2 },
      ]}
    >
      <div data-testid="production-approvals-page">
      <div className="sf-surface p-4 mb-4 flex items-center gap-3">
        <ClipboardCheck className="w-5 h-5 text-orange-600" />
        <div>
          <h1 className="text-xl font-bold tracking-tight">Pending Approvals</h1>
          <p className="text-xs text-zinc-500">Daily Production Reports awaiting your approval</p>
        </div>
      </div>

      <div>
        <div className="bg-white rounded-lg border shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">SO Nos</th>
                <th className="p-3 text-left">Customer</th>
                <th className="p-3 text-left">Created By</th>
                <th className="p-3 text-left">Submitted At</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-gray-500" data-testid="no-pending-reports">No reports waiting for approval.</td></tr>}
              {reports.map(r => (
                <tr key={r.id} className="border-b hover:bg-gray-50" data-testid={`pending-row-${r.id}`}>
                  <td className="p-3 font-medium">{r.report_date}</td>
                  <td className="p-3 text-gray-600">{(r.po_numbers || []).join(", ")}</td>
                  <td className="p-3 text-gray-600">{(r.customer_names || []).join(", ")}</td>
                  <td className="p-3 text-gray-600">{r.created_by_name || r.created_by}</td>
                  <td className="p-3 text-gray-500">{r.submitted_at ? new Date(r.submitted_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "-"}</td>
                  <td className="p-3">
                    <Button size="sm" onClick={() => navigate(`/safety/production-reports/${r.id}`)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`review-report-${r.id}`}>
                      <Eye className="w-3 h-3 mr-1" /> Review
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </SafetyShell>
  );
}
